import { CareType, CareTypes } from 'src/constants/CareTypes';
import { CareZone, Unit } from 'src/store/sageAdminApi';

export const DefaultCareTypeGroups: CareType[][] = [
    [CareTypes.AssistedLiving, CareTypes.MemoryCare, CareTypes.IndependentLiving],
    [CareTypes.LongTermCare, CareTypes.ShortTermRehab, CareTypes.SkilledNursing],
];

type CareZoneWithUnits = CareZone & {
    units: Unit[];
};

export function getCareZoneGroups(
    careZones: CareZoneWithUnits[] = [],
    careTypeGroups: CareType[][] = DefaultCareTypeGroups
) {
    if (!careZones || !careZones.length) {
        return {
            groups: [],
            indexMap: new Map<string, number>(),
        };
    }

    // Make a map of careType to group index
    let careTypeToGroupMap = careTypeGroups.reduce((careTypeToGroupMap, group, index) => {
        group.forEach((careType) => {
            if (careTypeToGroupMap.get(careType) !== undefined) {
                // If the careType is already in a group, do not add it again
                return;
            }

            careTypeToGroupMap.set(careType, index);
        });
        return careTypeToGroupMap;
    }, new Map<CareType, number>());

    // Create an initial array of empty arrays for each careTypeGroup
    let careZoneGroups = careTypeGroups.map(() => [] as CareZoneWithUnits[]);
    let unspecifiedGroup = [] as CareZoneWithUnits[];

    for (const careZone of careZones) {
        const careZoneCareTypes = new Set<CareType>();

        // primarily check for the care types of the units in the care zone
        if ((careZone.units ?? []).length > 0) {
            careZone.units.forEach((unit) => {
                const careType = unit?.careType || unit?.ward;
                if (careType) {
                    careZoneCareTypes.add(careType as CareType);
                }
            });
        } else {
            // fall back to unit tags for care type information
            const { wards = [], careTypes = [] } = careZone?.unitTags ?? {};
            [...wards, ...careTypes].forEach((careType) =>
                careZoneCareTypes.add(careType as CareType)
            );
        }

        if (!careZoneCareTypes.size) {
            // If there is no care type information, add to the unspecified group
            unspecifiedGroup.push(careZone);
            continue;
        }

        // Determine which groups this care zone belongs to
        const groupIndices = new Set<number>();

        for (const careType of careZoneCareTypes) {
            const groupIndex = careTypeToGroupMap.get(careType);

            if (groupIndex !== undefined) {
                groupIndices.add(groupIndex);
            }
        }

        // If care zone belongs to exactly one group, add it there; otherwise add to unspecified
        if (groupIndices.size === 1) {
            const [groupIndex] = groupIndices;
            careZoneGroups[groupIndex].push(careZone);
        } else {
            // Multiple groups or no groups (all care types are in the unspecified group)
            unspecifiedGroup.push(careZone);
        }
    }

    // Remove empty groups + deduplicate care zones
    const nonEmptyGroups = [...careZoneGroups, unspecifiedGroup]
        .filter((group) => group.length > 0)
        .map((group) => {
            // Deduplicate by care zone ID
            const careZoneMap = group.reduce((map, careZone) => {
                if (!map.has(careZone.id!)) {
                    map.set(careZone.id!, careZone);
                }
                return map;
            }, new Map<string, CareZoneWithUnits>());
            return Array.from(careZoneMap.values());
        });

    const careZoneToGroupMap = nonEmptyGroups.reduce((careZoneToGroupMap, group, index) => {
        group.forEach((careZone) => {
            // eslint-disable-next-line no-param-reassign
            careZoneToGroupMap.set(careZone.id!, index);
        });
        return careZoneToGroupMap;
    }, new Map<string, number>());

    return {
        groups: nonEmptyGroups,
        indexMap: careZoneToGroupMap,
    };
}

interface CareZoneGroupsByFacilityType {
    [key: string]: CareZone[];
}

export function getCareZonesByFacility(careZones: CareZone[]): CareZoneGroupsByFacilityType {
    return (
        careZones?.reduce((acc: CareZoneGroupsByFacilityType, careZone: CareZone) => {
            const { facilityId } = careZone;
            if (!facilityId) return acc;

            if (acc[facilityId]) {
                acc[facilityId].push(careZone);
                return acc;
            } else {
                return {
                    ...acc,
                    [facilityId]: [careZone],
                };
            }
        }, {} as CareZoneGroupsByFacilityType) || {}
    );
}

const CareZoneNameAbbreviations: { [key: string]: string } = {
    'assisted living': 'AL',
    'independent living': 'IL',
    'memory care': 'MC',
    'long term care': 'LTC',
    'short term rehab': 'STR',
    'skilled nursing': 'SN',
};
const formatter = new Intl.ListFormat('en', { style: 'short', type: 'conjunction' });

export function getCareZoneAbbreviation(careZone: CareZone): string {
    return (
        CareZoneNameAbbreviations[careZone?.name?.toLocaleLowerCase() || ''] || careZone?.name || ''
    );
}

export function getCareZonesDisplayText(careZones: CareZone[] = []): string {
    if (!careZones?.length) {
        return '';
    }

    // Abbreviate when listing multiple zones; a single zone reads better spelled out.
    const careZoneDisplayNames =
        careZones.length > 1
            ? careZones.map(getCareZoneAbbreviation)
            : careZones.map((careZone) => careZone?.name);

    return formatter.format(careZoneDisplayNames.filter((name): name is string => !!name));
}

const includesUnitTag = (tags: string[], value?: string) =>
    !!value && tags.some((tag) => tag.trim().toLowerCase() === value.trim().toLowerCase());

export function isUnitInCareZone(unit: Unit, careZone: CareZone) {
    const {
        unitRids = [],
        wards = [],
        careTypes = [],
        buildings = [],
        wings = [],
        floors = [],
    } = careZone.unitTags ?? {};

    return (
        // In unitRids list
        (unit.id && unitRids.includes(unit.id)) ||
        // In wards list
        (unit.ward && wards.includes(unit.ward)) ||
        // In careTypes list
        (unit.careType && careTypes.includes(unit.careType)) ||
        // In buildings list
        includesUnitTag(buildings, unit.building) ||
        // In wings list
        includesUnitTag(wings, unit.wing) ||
        // In floors list
        includesUnitTag(floors, unit.floor)
    );
}
